import { get, post } from 'services/api';
import getLocale from 'helpers/getLocale';
import {
  ASSESSMENT_LOAD,
  ASSESSMENT_SUBMIT_ANSWER,
  ASSESSMENT_GET_RESULT,
  ASSESSMENT_RESET,
  ASSESSMENT_SET_PHASE,
  ASSESSMENT_SET_QUESTION,
  ASSESSMENT_NEXT_QUESTION,
  ASSESSMENT_VALIDATE_CODE,
  ASSESSMENT_PATCH_SESSION,
  ASSESSMENT_LINK_USER,
  ASSESSMENT_GET_ME,
  ASSESSMENT_EMAIL_RESULTS,
  ASSESSMENT_EMAIL_RESUME_LINK,
  ASSESSMENT_WONDERLIC_START,
} from './types/assessment';

// Assessment runs on a per-session token (sessionId + sessionToken), not on
// Firebase auth. A 401 from these endpoints means the session token is invalid
// for the route, not that the user should be logged out — opt out of the
// global logout cascade.
const ASSESSMENT_OPTS = { skipAuthLogout: true };

// Pick the locale for the assessment in priority order:
//   1. The user's explicit choice from the in-flow LanguageDropdown
//      (sessionStorage `assessment_lang`).
//   2. Whatever URL-derived locale getLocale() returns.
//   3. 'ua' as the final fallback for profitroll.com.ua.
// getLocale() alone is wrong here: profitroll.com.ua has no /:locale prefix,
// so it always returns 'ua' even after the user picks Russian or English in
// the dropdown.
const ASSESSMENT_LOCALES = ['ua', 'en', 'ru'];
const assessmentLocale = () => {
  try {
    const stored = sessionStorage.getItem('assessment_lang');
    if (stored && ASSESSMENT_LOCALES.includes(stored)) return stored;
  } catch (e) { /* sessionStorage may be unavailable */ }
  const url = getLocale();
  if (url && ASSESSMENT_LOCALES.includes(url)) return url;
  return 'ua';
};

// Starting an anonymous session must be an explicit action (the demo). If a
// redeemed voucher loses either credential, creatingSession would detach every
// subsequent answer from the voucher and hide its completion from the owner.
export const assessmentLoad = ({ createSession = false } = {}) => (dispatch) => {
  const hasSession = !!sessionStorage.getItem('sessionId') && !!sessionStorage.getItem('sessionToken');
  if (!hasSession && !createSession) {
    const error = new Error('Assessment session credentials are missing');
    error.code = 'ASSESSMENT_SESSION_REQUIRED';
    return Promise.reject(error);
  }
  const body = createSession ? { creatingSession: true, demo: true } : {};
  const locale = assessmentLocale();
  const url = `assessment/load?locale=${locale}`;
  return post(url, body, ASSESSMENT_LOAD, dispatch, {}, ASSESSMENT_OPTS);
};

// `email` is optional and only used to gate re-viewing the results of a
// completed voucher whose original session captured an email. Backend returns
// `{ reviewMode: 'awaiting_email' | 'email_mismatch' | 'granted', ... }` in
// that path; the welcome screen branches on it.
export const assessmentValidateCode = (code, email) => (dispatch) =>
  post(
    'assessment/start',
    email ? { code, email } : { code },
    ASSESSMENT_VALIDATE_CODE,
    dispatch,
    {},
    ASSESSMENT_OPTS,
  );

// Redeem a voucher by its long token — used by the /assessment/redeem/:token
// email deep-link. The backend `assessment/start` endpoint accepts token or code.
export const assessmentValidateToken = (token, email) => (dispatch) =>
  post(
    'assessment/start',
    email ? { token, email } : { token },
    ASSESSMENT_VALIDATE_CODE,
    dispatch,
    {},
    ASSESSMENT_OPTS,
  );

// Sends as POST (not PATCH) because some upstream CORS configurations don't
// list PATCH in Access-Control-Allow-Methods. The backend exposes both verbs
// for /assessment/session — see routes/assessment.routes.js.
export const assessmentPatchSession = (fields) => (dispatch) =>
  post('assessment/session', fields, ASSESSMENT_PATCH_SESSION, dispatch, {}, ASSESSMENT_OPTS);

export const assessmentLinkUser = () => (dispatch) =>
  post('assessment/link-user', {}, ASSESSMENT_LINK_USER, dispatch, {}, ASSESSMENT_OPTS);

export const assessmentGetMe = () => (dispatch) =>
  get('assessment/me', ASSESSMENT_GET_ME, dispatch, undefined, ASSESSMENT_OPTS);

// `ttaMs` is the client-measured time-to-answer (performance.now() delta).
// Null/undefined → omit from the body and let the server compute its own
// delta. Snake_case on the wire to match the Python Pydantic model.
export const assessmentSubmitAnswer = (questionId, answer, ttaMs) => (dispatch) => {
  const body = { questionId, answer };
  if (typeof ttaMs === 'number' && Number.isFinite(ttaMs) && ttaMs >= 0) {
    body.tta_ms = Math.round(ttaMs);
  }
  return post('assessment/answer', body, ASSESSMENT_SUBMIT_ANSWER, dispatch, {}, ASSESSMENT_OPTS);
};

export const assessmentGetResult = () => (dispatch) => {
  const locale = assessmentLocale();
  // Feedback is editable after scoring; a cached response would restore stale
  // ratings when the participant returns to the report in the same browser.
  return get(`assessment/result?locale=${locale}`, ASSESSMENT_GET_RESULT, dispatch, undefined, { ...ASSESSMENT_OPTS, skipCache: true });
};

export const assessmentReset = () => (dispatch) =>
  post('assessment/reset', {}, ASSESSMENT_RESET, dispatch, {}, ASSESSMENT_OPTS);

export const assessmentEmailResults = (email, userInfo) => (dispatch) =>
  post('assessment/email-results', { email, userInfo }, ASSESSMENT_EMAIL_RESULTS, dispatch, {}, ASSESSMENT_OPTS);

// Send a "you started the assessment, here's a link to come back" email after
// the user enters their email in the info wizard. Best-effort — backend
// returns `{ sent: false, reason: 'no-voucher' }` for demo sessions, which the
// caller treats as a no-op.
export const assessmentEmailResumeLink = (email, userInfo) => (dispatch) =>
  post('assessment/email-resume-link', { email, userInfo }, ASSESSMENT_EMAIL_RESUME_LINK, dispatch, {}, ASSESSMENT_OPTS);

// Idempotent: stamps the Wonderlic phase start time on the server. Returns
// `{ phase_started_at, time_limit_seconds, server_now }` — the frontend uses
// these to render an authoritative countdown that survives client-clock skew
// and refreshes.
export const assessmentWonderlicStart = () => (dispatch) =>
  post('assessment/wonderlic/start', {}, ASSESSMENT_WONDERLIC_START, dispatch, {}, ASSESSMENT_OPTS);

export const assessmentSetPhase = (index) => (dispatch) =>
  dispatch({ type: ASSESSMENT_SET_PHASE, payload: index });

export const assessmentSetQuestion = (index) => (dispatch) =>
  dispatch({ type: ASSESSMENT_SET_QUESTION, payload: index });

export const assessmentNextQuestion = () => (dispatch) =>
  dispatch({ type: ASSESSMENT_NEXT_QUESTION });
